// src/lib/txErrors.js
//
// One place that turns raw wallet/contract errors into something a human
// can act on. Every surface where the user signs a transaction (room
// tickets, XPass swaps, live-room joins, collection unlocks, call &
// media purchases) funnels its catch block through classifyTxError so
// the copy is consistent — and, critically, a LOW BALANCE stops being
// "execution reverted: insufficient funds for gas * price + value" and
// becomes "Your wallet balance is too low" with a plain notice that the
// wallet needs ETH on Base (fund it from an exchange). These are the legacy
// on-chain flows; the Lunexx balance has its own Add Funds modal.
//
//   const c = classifyTxError(err);
//   setError(c.message);
//   setNeedsTopUp(c.topUp);       // render <TopUpCta /> (the notice) when true
//
// Or, where the raw error object is available at render time:
//   <TxErrorNotice error={err} />
//
// Detection walks the full viem/wagmi CAUSE CHAIN — the useful signal
// (InsufficientFundsError, UserRejectedRequestError, code 4001) is often
// two or three `.cause` levels below the error a hook hands back, so
// matching only err.message misses most real cases.

import React from 'react';
import { Wallet } from 'lucide-react';

// ---------------------------------------------------------------------
// CLASSIFICATION
// ---------------------------------------------------------------------

const MAX_CAUSE_DEPTH = 8;

/** Collect names/messages/codes from the whole cause chain. */
function unwrap(err) {
  const texts = [];
  const codes = new Set();
  let e = err;
  let depth = 0;
  while (e && depth < MAX_CAUSE_DEPTH) {
    for (const key of ['name', 'shortMessage', 'message', 'details', 'reason']) {
      if (typeof e[key] === 'string' && e[key]) texts.push(e[key]);
    }
    if (e.code !== undefined) codes.add(e.code);
    e = e.cause;
    depth += 1;
  }
  return { text: texts.join(' | '), codes };
}

const INSUFFICIENT_RE =
  /insufficientfundserror|insufficient funds|insufficient balance|exceeds the balance of the account|not enough (eth|funds|balance)|gas required exceeds allowance/i;

const REJECTED_RE =
  /userrejectedrequesterror|user rejected|user denied|rejected the request|denied transaction|request was declined|action_rejected|user cancel/i;

const WRONG_CHAIN_RE =
  /switch to base sepolia|did not switch|chain mismatch|chainmismatcherror|wrong network|unsupported chain/i;

const NO_WALLET_RE = /no wallet found|no account connected|no provider/i;

/**
 * @returns {{ kind: 'insufficient_funds'|'rejected'|'wrong_chain'|'no_wallet'|'other',
 *             message: string, topUp: boolean }}
 */
export function classifyTxError(err) {
  if (typeof err === 'string') err = { message: err };
  const { text, codes } = unwrap(err);

  // Order matters: some wallets wrap a low-balance pre-check inside a
  // -32000/-32603 "internal" error whose outer message mentions nothing.
  if (INSUFFICIENT_RE.test(text)) {
    return {
      kind: 'insufficient_funds',
      message: 'Your wallet balance is too low for this transaction.',
      topUp: true,
    };
  }
  if (codes.has(4001) || codes.has('ACTION_REJECTED') || REJECTED_RE.test(text)) {
    return { kind: 'rejected', message: 'Transaction cancelled', topUp: false };
  }
  if (WRONG_CHAIN_RE.test(text)) {
    return {
      kind: 'wrong_chain',
      message: 'Wrong network — switch your wallet to Base Sepolia and try again.',
      topUp: false,
    };
  }
  if (NO_WALLET_RE.test(text)) {
    return {
      kind: 'no_wallet',
      message: 'No wallet connected — connect your wallet to continue.',
      topUp: false,
    };
  }

  const fallback =
    err?.shortMessage || err?.cause?.shortMessage || err?.message || 'Transaction failed';
  // Raw node errors can be paragraphs long — keep the first sentence-ish chunk.
  const message = String(fallback).split('\n')[0].slice(0, 160);
  return { kind: 'other', message, topUp: false };
}

// ---------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------

/**
 * The low-wallet-balance notice for on-chain flows: the wallet needs ETH on
 * Base, funded from an exchange. Not a link to anything money-related.
 */
export function TopUpCta({ className = '' }) {
  return (
    <p className={`inline-flex items-center gap-1.5 text-[11px] text-zinc-400 ${className}`}>
      <Wallet size={12} />
      Your wallet needs ETH on Base; fund it from an exchange.
    </p>
  );
}

/**
 * Drop-in error block: classified message + Top-up CTA when the failure
 * was a low balance. For sites that have the raw error object at render
 * time (SwapBox, PreCallModal).
 */
export function TxErrorNotice({ error, className = '' }) {
  if (!error) return null;
  const c = typeof error === 'object' && error.kind && error.message
    ? error // already-classified shape passes through
    : classifyTxError(error);
  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      <p className="text-[11px] text-rose-400 text-center">{c.message}</p>
      {c.topUp && <TopUpCta />}
    </div>
  );
}
